"use client";

import { useState } from "react";
import { CandlestickChart, ShieldCheck, TrendingUp, ArrowRight, CheckCircle2, Wallet } from "lucide-react";
import { BROKERAGE_PLANS, calculateAnnualCost, formatINR } from "@/lib/demos/stockBrokingData";

interface StockBrokingHeroProps {
  onOpenAccount: (planId: string) => void;
}

export function StockBrokingHero({ onOpenAccount }: StockBrokingHeroProps) {
  const [planId, setPlanId] = useState("value");
  const [orders, setOrders] = useState(30);
  const [sip, setSip] = useState(10000);

  const calc = calculateAnnualCost(planId, orders, sip);

  return (
    <section className="relative overflow-hidden pt-12 pb-20 lg:pt-16 lg:pb-28">
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[650px] h-[350px] bg-gradient-to-tr from-emerald-600/15 via-teal-600/10 to-transparent blur-3xl -z-10 pointer-events-none rounded-full" />
      <div className="absolute top-1/3 -right-20 w-96 h-96 bg-emerald-500/10 blur-3xl -z-10 pointer-events-none rounded-full" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          {/* Left */}
          <div className="lg:col-span-6 space-y-6">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-600 text-xs font-semibold">
              <ShieldCheck className="w-4 h-4" />
              <span>SEBI Registered · NSE · BSE · MCX · NSDL Depository Member</span>
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-slate-900 tracking-tight leading-[1.1]">
              Open a <span className="bg-gradient-to-r from-emerald-600 via-teal-500 to-emerald-600 bg-clip-text text-transparent">Demat &amp; Trading</span> account in 10 minutes
            </h1>

            <p className="text-base sm:text-lg text-slate-600 leading-relaxed">
              100% paperless Aadhaar e-KYC. Invest in Equity, F&amp;O, Commodity, Currency, IPOs and direct Mutual Funds — with a dedicated Relationship Manager and zero brokerage on delivery.
            </p>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-2">
              {[
                { v: "₹0", l: "Delivery Brokerage" },
                { v: "10 min", l: "e-KYC Onboarding" },
                { v: "4x", l: "MTF Margin" },
                { v: "0%", l: "Direct MF Commission" },
              ].map((s) => (
                <div key={s.l} className="p-3 rounded-xl bg-white border border-slate-200">
                  <div className="text-xl sm:text-2xl font-black text-emerald-600">{s.v}</div>
                  <div className="text-[11px] text-slate-500 uppercase tracking-wide font-medium mt-0.5">{s.l}</div>
                </div>
              ))}
            </div>

            <div className="p-4 rounded-xl bg-emerald-50 border border-emerald-500/20 flex items-start gap-3.5">
              <div className="w-8 h-8 rounded-lg bg-emerald-500/10 flex items-center justify-center shrink-0 mt-0.5">
                <TrendingUp className="w-4 h-4 text-emerald-600" />
              </div>
              <div>
                <h4 className="text-xs font-bold text-emerald-700 uppercase tracking-wider">Live Onboarding Pipeline</h4>
                <p className="text-xs text-slate-600 mt-0.5">
                  Submitting your details instantly creates a lead in our Broking CRM and triggers a WhatsApp KYC link — routed to a Relationship Manager at your nearest branch.
                </p>
              </div>
            </div>
          </div>

          {/* Right: estimator */}
          <div className="lg:col-span-6">
            <div className="relative rounded-2xl bg-white border border-slate-200 p-6 sm:p-8 shadow-2xl shadow-emerald-950/30 backdrop-blur-xl">
              <div className="flex items-center justify-between border-b border-slate-200 pb-5 mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
                    <CandlestickChart className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-900 text-base">Brokerage &amp; Cost Estimator</h3>
                    <p className="text-xs text-slate-500">See your indicative annual cost</p>
                  </div>
                </div>
                <span className="text-[11px] font-semibold uppercase tracking-wider px-2.5 py-1 rounded-full bg-emerald-500/10 text-emerald-600 border border-emerald-500/20">Instant</span>
              </div>

              {/* Plan */}
              <div className="space-y-2 mb-6">
                <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider block">Choose your plan</label>
                <div className="grid grid-cols-3 gap-2">
                  {BROKERAGE_PLANS.map((p) => {
                    const on = p.id === planId;
                    return (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => setPlanId(p.id)}
                        className={`p-2.5 rounded-xl text-left border transition-all ${on ? "bg-emerald-500/15 border-emerald-500 text-slate-900 shadow-sm shadow-emerald-500/20" : "bg-slate-50 border-slate-200 text-slate-500 hover:border-slate-300"}`}
                      >
                        <div className="font-bold text-[11px] leading-tight">{p.title}</div>
                        <div className="text-[9px] text-emerald-600/90 mt-0.5">{p.badge}</div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Orders slider */}
              <div className="space-y-2 mb-6">
                <div className="flex items-center justify-between text-xs font-semibold text-slate-600">
                  <span className="uppercase tracking-wider">Intraday / F&amp;O orders / month</span>
                  <span className="text-emerald-600 font-bold text-sm bg-emerald-500/10 px-2.5 py-0.5 rounded border border-emerald-500/20">{orders}</span>
                </div>
                <input type="range" min={0} max={300} step={5} value={orders} onChange={(e) => setOrders(Number(e.target.value))} className="w-full h-2 bg-slate-100 rounded-lg appearance-none cursor-pointer accent-emerald-500" />
                <div className="flex justify-between text-[10px] text-slate-500 font-medium"><span>0</span><span>Casual</span><span>Active</span><span>300</span></div>
              </div>

              {/* SIP slider */}
              <div className="space-y-2 mb-6">
                <div className="flex items-center justify-between text-xs font-semibold text-slate-600">
                  <span className="uppercase tracking-wider">Monthly SIP</span>
                  <span className="text-teal-700 font-bold text-sm bg-teal-500/10 px-2.5 py-0.5 rounded border border-teal-500/20">{formatINR(sip)}</span>
                </div>
                <input type="range" min={0} max={100000} step={500} value={sip} onChange={(e) => setSip(Number(e.target.value))} className="w-full h-2 bg-slate-100 rounded-lg appearance-none cursor-pointer accent-teal-500" />
              </div>

              {/* Projection */}
              <div className="rounded-xl bg-slate-50 border border-slate-200 p-4 mb-6 grid grid-cols-2 gap-4 divide-x divide-slate-200">
                <div>
                  <span className="text-[10px] text-slate-500 uppercase tracking-wider block font-semibold">Est. annual brokerage</span>
                  <div className="text-2xl font-black text-slate-900 mt-1">{formatINR(calc.total)}<span className="text-xs font-normal text-slate-500"> / yr</span></div>
                  <span className="text-[10px] text-emerald-600 font-medium">incl. ₹{calc.plan.amc} AMC · ₹0 on delivery</span>
                </div>
                <div className="pl-4">
                  <span className="text-[10px] text-slate-500 uppercase tracking-wider block font-semibold">Invested via SIP / yr</span>
                  <div className="text-xl font-bold text-teal-700 mt-1">{formatINR(calc.annualSip)}</div>
                  <span className="text-[10px] text-slate-500 font-medium flex items-center gap-1"><Wallet className="w-3 h-3" /> 0% commission (direct)</span>
                </div>
              </div>

              <button
                type="button"
                onClick={() => onOpenAccount(planId)}
                className="w-full py-3.5 px-6 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-400 hover:to-teal-500 text-white font-bold text-sm tracking-wide shadow-lg shadow-emerald-500/25 flex items-center justify-center gap-2 group transition-all"
              >
                <span>Open my Demat account</span>
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </button>

              <div className="flex items-center justify-center gap-4 mt-3 text-[11px] text-slate-500 flex-wrap">
                <span className="flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" /> Aadhaar e-KYC</span>
                <span className="flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" /> No account-opening fee</span>
                <span className="flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" /> Dedicated RM</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
